import { useMemo } from 'react';
import { rage, useObservable } from '~/rage';
import { Subject } from '~/rage/utils';
import {
	ClothesType,
	equipmentSlotByClothesType,
	InventoryCell,
	InventoryType,
	Item,
	ItemAction,
	ItemType,
	PackedInventory,
	PackedItemStack,
} from '~/shared';
import {
	clothesPlaceholders,
	getClothesTypeName,
} from '../components/Inventory/Inventory.model';

const resolveBaseId = (fullId: string): string => {
	if (!fullId) return '';
	if (fullId.includes('-equip-')) return fullId.split('-equip-')[0];
	if (fullId.includes('-split-')) return fullId.split('-split-')[0];
	return fullId;
};

const adjustActions = (base: any[], count: number) => {
	const actions = [...base];
	const hasSplit = actions.includes(ItemAction.SPLIT);
	const dropIdx = actions.indexOf(ItemAction.DROP);
	if (count > 1) {
		if (!hasSplit) {
			if (dropIdx !== -1) actions.splice(dropIdx, 0, ItemAction.SPLIT);
			else actions.push(ItemAction.SPLIT);
		}
	} else {
		if (hasSplit) actions.splice(actions.indexOf(ItemAction.SPLIT), 1);
	}
	return actions;
};

const buildCell = (
	stack: PackedItemStack | null | undefined,
	position: number,
	itemInfo: Partial<Item> | null,
): InventoryCell => {
	if (!stack) {
		return { id: `empty-${position}`, position } as InventoryCell;
	}

	const baseId = resolveBaseId(stack.id);

	const templateFallback =
		itemInfo ?? ({ name: baseId, count: stack.count } as any);

	const available = adjustActions(
		templateFallback.availableActions ?? templateFallback.baseActions ?? [],
		stack.count,
	);

	const clothesType = stack.data?.clothes?.type;
	const clothesTypeName = clothesType ? getClothesTypeName(clothesType) : null;

	let finalName = templateFallback.name;
	if (templateFallback.subName) {
		if (
			templateFallback.subName.includes('«') ||
			templateFallback.subName.includes('»')
		) {
			finalName = clothesTypeName || '';
		} else {
			finalName = '';
		}
	}

	const mergedData = {
		...(templateFallback as any).data,
		...stack.data,
	};

	return {
		id: stack.id,
		position,
		item: {
			...templateFallback,
			id: stack.id,
			name: finalName,
			count: stack.count,
			availableActions: available as any,
			usageLeft: stack.data?.breakable?.durability ?? undefined,
			data: mergedData,
		} as unknown as Item,
	} as InventoryCell;
};

const useInventoryCells = () => {
	const inventoryIds = useObservable(rage.inventory.getInventoriesId());

	const secondaryInventoryId = useObservable(
		rage.inventory.getSecondaryInventoryId(),
	);
	const secondaryInventoryType = useObservable(
		rage.inventory.getSecondaryInventoryType(),
	);

	const playerInventory = useObservable(
		rage.inventory.getInventoryByType(InventoryType.PLAYER),
	);
	const characterInventory = useObservable(
		rage.inventory.getInventoryByType(InventoryType.EQUIPMENT),
	);
	const hotbarInventory = useObservable(
		rage.inventory.getInventoryByType(InventoryType.HOTBAR),
	);
	const backpackInventory = useObservable(
		rage.inventory.getInventoryByType(InventoryType.BACKPACK),
	);
	const exchangeInventory = useObservable(
		rage.inventory.getInventoryByType(InventoryType.EXCHANGE),
	);
	const secondaryInventory = useObservable(
		secondaryInventoryType !== null
			? rage.inventory.getInventoryByType(secondaryInventoryType)
			: new Subject<PackedInventory | null>(null),
	);
	const fromExchangeInventory = useObservable(
		rage.inventory.getFromExchangeInventory(),
	);
	const toExchangeInventory = useObservable(
		rage.inventory.getToExchangeInventory(),
	);

	const hasBackpack = useObservable(rage.inventory.hasBackpack());

	const inventoryCells = useMemo(
		() =>
			Array.from({ length: playerInventory?.capacity ?? 0 }).map((_, idx) => {
				const stack = playerInventory?.items[idx] ?? null;
				return buildCell(
					stack,
					idx + 1,
					rage.inventory.getItemInfoById(stack?.id ?? ''),
				);
			}),
		[playerInventory],
	);

	const characterCells = useMemo(
		() =>
			Array.from({ length: characterInventory?.capacity ?? 0 }).map(
				(_, idx) => {
					const stack = characterInventory?.items[idx];
					const placeholder = clothesPlaceholders?.[idx];
					const cell = buildCell(
						stack,
						idx + 1,
						rage.inventory.getItemInfoById(stack?.id ?? ''),
					);
					return {
						...cell,
						id: `char-${idx + 1}`,
						position: idx + 1,
						placeholderImage: placeholder?.placeholderImage ?? '',
						placeholderText: placeholder?.placeholderText ?? '',
						height: placeholder.height,
						allowedTypes:
							idx === equipmentSlotByClothesType[ClothesType.BACKPACK]
								? [ItemType.BACKPACK]
								: [ItemType.CLOTHES],
					};
				},
			),
		[characterInventory],
	);

	const hotbarCells = useMemo(
		() =>
			Array.from({ length: hotbarInventory?.capacity ?? 0 }).map((_, idx) => {
				const stack = hotbarInventory?.items[idx];
				const cell = buildCell(
					stack,
					idx + 1,
					rage.inventory.getItemInfoById(stack?.id ?? ''),
				);
				return { ...cell, id: `quick-${idx + 1}`, position: idx + 1 };
			}),
		[hotbarInventory],
	);

	const backpackCells = useMemo(
		() =>
			Array.from({
				length: Math.max(
					10,
					Math.ceil((backpackInventory?.capacity ?? 0) / 5) * 5,
				),
			}).map((_, idx) => {
				const stack = backpackInventory?.items[idx];
				const cell = buildCell(
					stack,
					idx + 1,
					rage.inventory.getItemInfoById(stack?.id ?? ''),
				);
				return {
					...cell,
					id: `back-${idx + 1}`,
					position: idx + 1,
					isClosed:
						!hasBackpack ||
						(backpackInventory ? idx >= backpackInventory.capacity : true),
				};
			}),
		[backpackInventory, hasBackpack],
	);

	const secondaryCells = useMemo(() => {
		if (!secondaryInventory) return [];
		return Array.from({ length: secondaryInventory?.capacity ?? 0 }).map(
			(_, idx) => {
				const stack = secondaryInventory?.items[idx];
				const cell = buildCell(
					stack,
					idx + 1,
					rage.inventory.getItemInfoById(stack?.id ?? ''),
				);
				return { ...cell, id: `trunk-${idx + 1}`, position: idx + 1 };
			},
		);
	}, [secondaryInventory]);

	const fromExchangeCells = useMemo(
		() =>
			Array.from({ length: fromExchangeInventory?.capacity ?? 0 }).map(
				(_, idx) => {
					const stack = fromExchangeInventory?.items[idx];
					const cell = buildCell(
						stack,
						idx + 1,
						rage.inventory.getItemInfoById(stack?.id ?? ''),
					);
					return { ...cell, id: `exchange-${idx + 1}`, position: idx + 1 };
				},
			),
		[fromExchangeInventory],
	);

	const toExchangeCells = useMemo(
		() =>
			Array.from({ length: toExchangeInventory?.capacity ?? 0 }).map(
				(_, idx) => {
					const stack = toExchangeInventory?.items[idx];
					const cell = buildCell(
						stack,
						idx + 1,
						rage.inventory.getItemInfoById(stack?.id ?? ''),
					);
					return { ...cell, id: `exchange-${idx + 1}`, position: idx + 1 };
				},
			),
		[toExchangeInventory],
	);

	return {
		inventoryIds,
		playerInventory,
		characterInventory,
		hotbarInventory,
		backpackInventory,
		exchangeInventory,
		secondaryInventory,
		fromExchangeInventory,
		toExchangeInventory,
		inventoryCells,
		characterCells,
		hotbarCells,
		backpackCells,
		secondaryCells,
		fromExchangeCells,
		toExchangeCells,
		hasBackpack,
		secondaryInventoryType,
		secondaryInventoryId,
	};
};

export { useInventoryCells };
